import ar from "@/locales/ar.json";
import en from "@/locales/en.json";

export type Language = "ar" | "en";

const LANGUAGE_STORAGE_KEY = "mandoob:language";
const RTL_LANGUAGES = new Set<Language>(["ar"]);

type Dictionary = Record<string, string>;

const dictionaries: Record<Language, Dictionary> = {
  ar: ar as Dictionary,
  en: en as Dictionary,
};

/**
 * Browser-locale detection: any preferred language starting with "ar" wins.
 * Used when the driver has no stored preference ("auto" mode).
 */
export function detectLanguage(): Language {
  try {
    if (typeof navigator === "undefined") return "en";
    const languages = navigator.languages?.length
      ? navigator.languages
      : [navigator.language];
    return languages.some((l) => l?.toLowerCase().startsWith("ar"))
      ? "ar"
      : "en";
  } catch {
    return "en";
  }
}

let currentLanguage: Language = readStoredLanguage() ?? detectLanguage();
const listeners = new Set<() => void>();

function readStoredLanguage(): Language | null {
  try {
    const stored = localStorage.getItem(LANGUAGE_STORAGE_KEY);
    return stored === "ar" || stored === "en" ? stored : null;
  } catch {
    return null;
  }
}

export function hasStoredLanguage(): boolean {
  return readStoredLanguage() !== null;
}

/** Back to "auto": forget the stored preference and re-detect. */
export function clearStoredLanguage(): void {
  try {
    localStorage.removeItem(LANGUAGE_STORAGE_KEY);
  } catch {
    // storage unavailable
  }
  setLanguage(detectLanguage(), false);
}

export function getLanguage(): Language {
  return currentLanguage;
}

export function getDirection(
  language: Language = currentLanguage,
): "ltr" | "rtl" {
  return RTL_LANGUAGES.has(language) ? "rtl" : "ltr";
}

export function subscribeLanguage(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

export function setLanguage(language: Language, persist = true): void {
  currentLanguage = language;
  if (persist) {
    try {
      localStorage.setItem(LANGUAGE_STORAGE_KEY, language);
    } catch {
      // storage unavailable — language lives for this page only
    }
  }
  if (typeof document !== "undefined") {
    document.documentElement.lang = language;
    document.documentElement.dir = getDirection(language);
  }
  for (const listener of listeners) listener();
}

// Mirrors abp.utils.formatString: "{0}" placeholders are replaced positionally.
function format(template: string, args: (string | number)[]): string {
  if (args.length === 0) return template;
  return template.replace(/\{(\d+)\}/g, (match, index: string) => {
    const value = args[Number(index)];
    return value === undefined ? match : String(value);
  });
}

// Localization lookup for non-React code. Components should call `useLanguage().t`.
export function translate(key: string, ...args: (string | number)[]): string {
  const template =
    dictionaries[currentLanguage][key] ?? dictionaries.en[key] ?? key;
  return format(template, args);
}
